import { useState } from "react";
import { useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Phone, Mail, MapPin, Clock } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const location = useLocation();
  const propertyAddress = location.state?.propertyAddress || "";
  
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    contact_time: "",
    message: "",
    interested_properties: propertyAddress,
    budget_range: "",
    timeline: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

const handleSubmit = async (e) => {
  e.preventDefault();
  
  // Validate email format
  const emailRegex = /^[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$/i;
  if (!emailRegex.test(formData.email)) {
    toast.error("Please enter a valid email address (e.g., name@example.com)");
    return;
  }
  
  setSubmitting(true);

  try {
    await axios.post(`${API}/contact`, formData);
    toast.success("Thank you for your inquiry! I'll be in touch soon.");
      setFormData({
        name: "",
        email: "",
        phone: "",
        contact_time: "",
        message: "",
        interested_properties: "",
        budget_range: "",
        timeline: "",
      });
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error("Something went wrong. Please try again or call directly.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <section className="bg-primary text-primary-foreground py-20">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 text-center">
          <h1 className="text-5xl md:text-6xl font-heading font-bold mb-6">Get In Touch</h1>
          <p className="text-xl text-primary-foreground/90 max-w-3xl mx-auto leading-relaxed">
            Ready to start your real estate journey? I'm here to help.
          </p>
        </div>
      </section>

      <section className="py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            <div className="lg:col-span-2">
              <div className="mb-12">
                <p className="text-sm uppercase tracking-widest text-accent mb-4 font-semibold">Contact Form</p>
                <h2 className="text-3xl md:text-4xl font-heading font-semibold text-primary mb-6">
                  Send Me a Message
                </h2>
                <p className="text-muted-foreground leading-relaxed">
                  Fill out the form below and I'll get back to you as soon as possible. You can also reach me directly via phone or email.
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="name" className="text-sm uppercase tracking-widest font-semibold mb-2 block">
                      Full Name *
                    </Label>
                    <Input
                      id="name"
                      required
                      value={formData.name}
                      onChange={(e) => handleChange("name", e.target.value)}
                      className="h-12 rounded-none border-border focus:border-primary"
                      data-testid="name-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="email" className="text-sm uppercase tracking-widest font-semibold mb-2 block">
                      Email Address *
                    </Label>
                    <Input
                      id="email"
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => handleChange("email", e.target.value)}
                      pattern="[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$"
                      title="Please enter a valid email address (e.g., name@example.com)"
                      placeholder="name@example.com"
                      className="h-12 rounded-none border-border focus:border-primary"
                      data-testid="email-input"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="phone" className="text-sm uppercase tracking-widest font-semibold mb-2 block">
                      Phone Number *
                    </Label>
                    <Input
                      id="phone"
                      type="tel"
                      required
                      value={formData.phone}
                      onChange={(e) => handleChange("phone", e.target.value)}
                      className="h-12 rounded-none border-border focus:border-primary"
                      data-testid="phone-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="contact_time" className="text-sm uppercase tracking-widest font-semibold mb-2 block">
                      Preferred Contact Time
                    </Label>
                    <Select value={formData.contact_time} onValueChange={(value) => handleChange("contact_time", value)}>
                      <SelectTrigger id="contact_time" className="h-12 rounded-none border-border focus:border-primary" data-testid="contact-time-select">
                        <SelectValue placeholder="Select Time" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Morning (9AM-12PM)">Morning (9AM-12PM)</SelectItem>
                        <SelectItem value="Afternoon (12PM-5PM)">Afternoon (12PM-5PM)</SelectItem>
                        <SelectItem value="Evening (5PM-8PM)">Evening (5PM-8PM)</SelectItem>
                        <SelectItem value="Anytime">Anytime</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <Label htmlFor="interested_properties" className="text-sm uppercase tracking-widest font-semibold mb-2 block">
                    Interested Properties
                  </Label>
                  <Input
                    id="interested_properties"
                    value={formData.interested_properties}
                    onChange={(e) => handleChange("interested_properties", e.target.value)}
                    placeholder="Enter MLS numbers or addresses"
                    className="h-12 rounded-none border-border focus:border-primary"
                    data-testid="interested-properties-input"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="budget_range" className="text-sm uppercase tracking-widest font-semibold mb-2 block">
                      Budget Range
                    </Label>
                    <Select value={formData.budget_range} onValueChange={(value) => handleChange("budget_range", value)}>
                      <SelectTrigger id="budget_range" className="h-12 rounded-none border-border focus:border-primary" data-testid="budget-range-select">
                        <SelectValue placeholder="Select Range" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Under $500,000">Under $500,000</SelectItem>
                        <SelectItem value="$500,000 - $1,000,000">$500,000 - $1,000,000</SelectItem>
                        <SelectItem value="$1,000,000 - $2,000,000">$1,000,000 - $2,000,000</SelectItem>
                        <SelectItem value="$2,000,000 - $3,000,000">$2,000,000 - $3,000,000</SelectItem>
                        <SelectItem value="Over $3,000,000">Over $3,000,000</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="timeline" className="text-sm uppercase tracking-widest font-semibold mb-2 block">
                      Timeline
                    </Label>
                    <Select value={formData.timeline} onValueChange={(value) => handleChange("timeline", value)}>
                      <SelectTrigger id="timeline" className="h-12 rounded-none border-border focus:border-primary" data-testid="timeline-select">
                        <SelectValue placeholder="Select Timeline" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Immediately">Immediately</SelectItem>
                        <SelectItem value="Within 1 month">Within 1 month</SelectItem>
                        <SelectItem value="1-3 months">1-3 months</SelectItem>
                        <SelectItem value="3-6 months">3-6 months</SelectItem>
                        <SelectItem value="6+ months">6+ months</SelectItem>
                        <SelectItem value="Just browsing">Just browsing</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <Label htmlFor="message" className="text-sm uppercase tracking-widest font-semibold mb-2 block">
                    Message *
                  </Label>
                  <Textarea
                    id="message"
                    required
                    value={formData.message}
                    onChange={(e) => handleChange("message", e.target.value)}
                    rows={6}
                    className="rounded-none border-border focus:border-primary resize-none"
                    placeholder="Tell me about your real estate needs..."
                    data-testid="message-input"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-primary text-primary-foreground hover:bg-primary/90 h-14 px-8 rounded-none uppercase tracking-widest text-sm font-semibold transition-all duration-300"
                  data-testid="submit-button"
                >
                  {submitting ? "Sending..." : "Send Message"}
                </Button>
              </form>
            </div>

            <div>
              <div className="sticky top-24 space-y-8">
                <div>
                  <p className="text-sm uppercase tracking-widest text-accent mb-4 font-semibold">Contact Information</p>
                  <h3 className="text-2xl font-heading font-semibold text-primary mb-6">Let's Connect</h3>
                  
                  <div className="space-y-6">
                    <div className="flex items-start" data-testid="contact-info-phone">
                      <div className="flex-shrink-0 w-12 h-12 bg-accent/10 flex items-center justify-center mr-4">
                        <Phone className="w-5 h-5 text-accent" />
                      </div>
                      <div>
                        <p className="text-sm uppercase tracking-widest text-muted-foreground mb-1">Phone</p>
                        <a href="tel:4168278717" className="text-lg font-semibold text-primary hover:text-accent transition-colors">
                          (416) 827-8717
                        </a>
                      </div>
                    </div>

                    <div className="flex items-start" data-testid="contact-info-email">
                      <div className="flex-shrink-0 w-12 h-12 bg-accent/10 flex items-center justify-center mr-4">
                        <Mail className="w-5 h-5 text-accent" />
                      </div>
                      <div>
                        <p className="text-sm uppercase tracking-widest text-muted-foreground mb-1">Email</p>
                        <a href="mailto:paul@murrayrealtor.com" className="text-lg font-semibold text-primary hover:text-accent transition-colors break-all">
                          paul@murrayrealtor.com
                        </a>
                      </div>
                    </div>

                    <div className="flex items-start" data-testid="contact-info-location">
                      <div className="flex-shrink-0 w-12 h-12 bg-accent/10 flex items-center justify-center mr-4">
                        <MapPin className="w-5 h-5 text-accent" />
                      </div>
                      <div>
                        <p className="text-sm uppercase tracking-widest text-muted-foreground mb-1">Office</p>
                        <p className="text-lg font-semibold text-primary">
                          Toronto, Ontario<br />
                          Greater Toronto Area
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start" data-testid="contact-info-hours">
                      <div className="flex-shrink-0 w-12 h-12 bg-accent/10 flex items-center justify-center mr-4">
                        <Clock className="w-5 h-5 text-accent" />
                      </div>
                      <div>
                        <p className="text-sm uppercase tracking-widest text-muted-foreground mb-1">Hours</p>
                        <p className="text-lg font-semibold text-primary">
                          Mon - Fri: 9AM - 8PM<br />
                          Sat - Sun: 10AM - 6PM
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-secondary/50 p-8">
                  <h3 className="text-lg font-heading font-semibold text-primary mb-4">Quick Response Guarantee</h3>
                  <p className="text-muted-foreground leading-relaxed">
                    I understand that timing is crucial in real estate. I personally respond to all inquiries within 2 hours during business hours.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
